import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Building2, Banknote, User, BookOpen, Calendar, X, Search } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loading, EmptyState } from "@/components/common/Loading";

const META = {
  personal: { Icon: User, label: "Person", round: true, href: (b) => (b.slug ? `/p/${b.slug}` : null) },
  business: { Icon: Building2, label: "Business", href: (b) => (b.slug ? `/b/${b.slug}` : null) },
  capital: { Icon: Banknote, label: "Capital", href: (b) => (b.slug ? `/capital/${b.slug}` : null) },
  resource: { Icon: BookOpen, label: "Resource", href: (b) => (b.slug ? `/resource/${b.slug}` : null) },
  event: { Icon: Calendar, label: "Event", href: (b) => (b.slug ? `/event/${b.slug}` : null) },
};

const TABS = [
  { key: "all", label: "All" },
  { key: "personal", label: "People" },
  { key: "business", label: "Businesses" },
  { key: "capital", label: "Capital" },
  { key: "resource", label: "Resources" },
  { key: "event", label: "Events" },
];

export default function Bookmarks() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/bookmarks"); setItems(data); } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (b) => {
    try {
      await api.post("/bookmarks", { item_type: b.item_type, item_id: b.item_id });
      setItems((prev) => prev.filter((x) => x.bookmark_id !== b.bookmark_id));
      toast.success("Removed");
    } catch { toast.error("Action failed"); }
  };

  const counts = useMemo(() => {
    const c = { all: items?.length || 0 };
    (items || []).forEach((b) => { c[b.item_type] = (c[b.item_type] || 0) + 1; });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (tab !== "all") list = list.filter((b) => b.item_type === tab);
    const needle = q.trim().toLowerCase();
    if (needle) list = list.filter((b) =>
      (b.display_name || "").toLowerCase().includes(needle) ||
      (b.headline || "").toLowerCase().includes(needle));
    return list;
  }, [items, tab, q]);

  if (!items) return <Loading />;

  return (
    <div data-testid="bookmarks-page">
      <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Bookmarks</h1>
      <p className="mt-1 text-muted-foreground">Profiles, resources and events you've saved.</p>

      {items.length > 0 && (
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`bookmarks-tab-${t.key}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-[560] transition-colors ${
                  tab === t.key ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground/70 hover:bg-secondary/80"
                }`}
              >
                {t.label}
                <span className={`rounded-full px-1.5 text-[11px] ${tab === t.key ? "bg-white/20" : "bg-background/70"}`}>{counts[t.key] || 0}</span>
              </button>
            ))}
          </div>
          <div className="relative sm:w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search saved…" className="pl-9" data-testid="bookmarks-search" />
          </div>
        </div>
      )}

      <div className="mt-6">
        {filtered.length === 0 ? (
          <EmptyState icon={Bookmark} title={items.length === 0 ? "No bookmarks yet" : "Nothing here"} description={items.length === 0 ? "Save profiles, resources and events to find them quickly later." : "Try a different filter or search."} testId="bookmarks-empty" />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((b) => {
              const meta = META[b.item_type] || META.personal;
              const href = meta.href(b);
              const img = b.headshot_url || b.profile_image_url;
              const card = (
                <Card className="flex h-full items-start gap-3 border-0 p-4" data-testid={`bookmark-${b.bookmark_id}`}>
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden bg-secondary ${meta.round ? "rounded-full" : ""}`}>
                    {img ? <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" /> : <meta.Icon className="h-5 w-5 text-muted-foreground" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-[600] text-foreground">{b.display_name || "Untitled"}</div>
                    {b.headline && <div className="truncate text-sm text-muted-foreground">{b.headline}</div>}
                    <Badge variant="secondary" className="mt-2">{meta.label}</Badge>
                  </div>
                  <button onClick={(e) => { e.preventDefault(); remove(b); }} className="text-muted-foreground hover:text-destructive" aria-label="Remove bookmark" data-testid={`bookmark-remove-${b.bookmark_id}`}><X className="h-4 w-4" /></button>
                </Card>
              );
              return href ? <Link key={b.bookmark_id} to={href}>{card}</Link> : <div key={b.bookmark_id}>{card}</div>;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
